// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import { Box, InputLabel, Stack } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseCheckbox, BaseSelect, Loading } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { TypeQuoteDetailEnum } from '~/utils/enum/common';
import { convertDataDetailQuotesForm } from '~/pages/common/quote/quotesForm/helper';
import { getDetailQuote, getListQuote } from '~/thunks/quote/quoteThunk';
import { IBaseOption, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import { DEFAULT_LIMIT_PAGE, DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA } from '~/utils/constants/common';
import { IFormQuotes, IQueryParamQuote, IQueryParamQuoteDetail, IQuoteDetail } from '~/utils/interface/quotes';
// Styles, images, icons
import styles from './TemplateQuoteForm.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const TemplateQuoteForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes']);
  const { id: quoteId } = useParams();
  const dispatch = useAppDispatch();

  const { control, setValue, reset } = useFormContext<IFormQuotes>();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo

  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const fetchOptions = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamQuote = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      isTemplate: true,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListQuote(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: item?.eventType?.name,
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const handleSelectTemplate = (selectedValue: IBaseOption | null) => {
    if (!selectedValue) return;

    const params: IQueryParamQuoteDetail = {
      quoteId: selectedValue?.value,
      type: TypeQuoteDetailEnum.OVERVIEW,
    };

    setIsLoading(true);
    dispatch(getDetailQuote(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const dataDetail = res?.data as IQuoteDetail;

        const convertData: IFormQuotes = convertDataDetailQuotesForm(dataDetail);

        reset(convertData);
        setValue('templateQuoteId', selectedValue.value);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <Box id='templateQuoteFormComponent' className={cx('container')}>
      <Stack direction={'column'} spacing={'10px'}>
        <InputLabel className={cx('label')}>{t('form_quotes_label_template')}</InputLabel>

        <Stack direction={'row'} gap={10}>
          <Controller
            name='templateQuoteId'
            control={control}
            render={({ field: { value } }) => (
              <BaseSelect
                isSearch
                type='loadMore'
                gap={8}
                width={'42%'}
                value={value}
                disabled={!!quoteId}
                styleType='contained'
                placeholder={t('form_quotes_placeholder_template')}
                callApiFn={fetchOptions}
                onChange={(selectedValue) => {
                  handleSelectTemplate(selectedValue);
                }}
              />
            )}
          />

          {!quoteId && (
            <Controller
              name={`isTemplate`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <BaseCheckbox
                  checked={value}
                  onChange={(checked) => {
                    onChange(checked);
                  }}
                  label={t('form_quotes_title_save_template')}
                />
              )}
            />
          )}
        </Stack>
      </Stack>

      {isLoading && <Loading />}
    </Box>
  );
};

export default TemplateQuoteForm;
